import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import { FLIGHTS_MAX_LEAD_TIME_DAYS } from 'constants/flight'
import Pane from 'components/Common/Pane'
import config from 'constants/config'
import isTimeLimited from 'lib/offer/isTimeLimited'
import OfferTimeLimitedText from '../OfferTimeLimitedText'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getCheapestSelectedPackageOptionWithDefault, arePricesLoading, isBNBLAvailable } from 'selectors/offerPage/offerPageSelectors'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'LuxKit/Button/TextButton'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import FormatCurrency from 'components/Common/FormatCurrency'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import GeoContext from 'contexts/geoContext'
import { isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import SecureWithDepositTagAndTooltip from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndTooltip'
import ReserveForZeroTag from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroTag'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import Group from 'components/utils/Group'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPriceLoadingSkeleton from 'LuxKit/PricePoints/PriceRowPriceLoadingSkeleton'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { setCustomerValue } from 'actions/AuthActions'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import { addFlightPrice, feesToObject, priceWithoutPropertyFees } from 'lib/offer/pricing'
import AccommodationPackageTilePricingBlocks from 'components/AccommodationKit/AccommodationPackageTilePricingBlocks'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import { sortBy } from 'lib/array/arrayUtils'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import { trackOfferPagePricingBlockViewOptionsClick } from 'components/OfferPage/analytics'
import FlightDealLabel from 'components/OfferPage/FlightDeal/FlightDealLabel'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import WalledContent from 'components/Common/WalledContent'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { trackClientEvent } from 'analytics/analytics'
import { useQsQueryParams } from 'hooks/useQueryParams'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import type { MultiRoomPackageTotals } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { getMultiRoomPackageTotals } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { getPriceDisplayFunction } from 'lib/offer/offerPageUtil'
import { FormattedMessage, useIntl } from 'react-intl'
import productLabels from 'i18n/productLabels'
import { computeRewardBundlePrice } from 'lib/maldivesCharter/classicRewardPricing'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlashHotelFlightDealMerchandisingSlot from './FlashHotelFlightDealMerchandisingSlot'
import getEffectiveHotelFlightPrice from 'lib/offer/getEffectiveHotelFlightPrice'
import useHotelFlightsCtaImpression from 'components/OfferPage/hooks/useHotelFlightsCtaImpression'

const SummaryPane = styled(Pane)`
  position: relative;

  > * + * {
    margin-top: ${rem(12)};
  }
`

const defaultMultiRoomTotals: MultiRoomPackageTotals = {
  flightPrice: 0,
  hotelMemberPrice: 0,
  hotelPlusFlightsMemberPrice: 0,
  hotelPlusFlightsPrice: 0,
  hotelPrice: 0,
  hotelValue: 0,
}

interface Props {
  offer: App.Offer
  rooms: Array<OfferPageRoom>
  className?: string
  cheapestSelectedOption: App.OfferPackageOptionView
  onViewPackageOptions: () => void
  calendarsLoading?: boolean
  checkIn?: string
  isBNBLAvailable: boolean
  isSpoofed: boolean
  duration: number
  flightOrigin?: App.Airport
  flightDestination?: App.Airport
  multiRoomPackageTotals: MultiRoomPackageTotals
  isMultiRoom: boolean
  priceMode: OfferPagePriceMode
}

function FlashOfferWithFlightsSummary(props: Props) {
  const {
    className,
    onViewPackageOptions,
    offer,
    cheapestSelectedOption: packageView,
    rooms,
    calendarsLoading,
    checkIn,
    isSpoofed,
    duration,
    flightOrigin,
    flightDestination,
    multiRoomPackageTotals,
    isMultiRoom,
    priceMode,
  } = props

  const intl = useIntl()
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const dispatch = useOfferPageDispatch()
  const appDispatch = useAppDispatch()

  const [deals] = useFlightDeals('all', {
    originAirportCode: flightOrigin?.code,
    destinationAirportCode: flightDestination?.code,
  })

  const activeDeal = useMemo(() => (
    deals.length > 0 ? sortBy(deals, (d: App.FlightDeal) => d.perAdultPrice, 'asc')[0] : undefined
  ), [deals])

  const {
    package: pkg,
    pricing: hotelPricing,
    memberPricing: hotelMemberPricing,
  } = packageView

  const isIndiaRegion = currentRegionCode === 'IN'
  const showPrices = packageView.capacityValid && packageView.hotelAvailable
  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const departureTooFarInFuture = useMemo(() => checkInMoment && checkInMoment.diff(moment.utc(), 'days') > FLIGHTS_MAX_LEAD_TIME_DAYS, [checkInMoment])

  const pricingToDisplay: App.PricingWithValue | undefined = hotelPricing
  const memberPricingToDisplay: App.PricingWithValue | undefined = hotelMemberPricing

  const fees = pricingToDisplay ? feesToObject(pricingToDisplay.fees) : {}

  const {
    totalFlightPrice,
    loading: flightPriceLoading,
    rewardAvailable,
    rewardSeatPointsTotal,
    rewardSeatCashCoPayTotal,
  } = useOfferPageFlightPrice()

  const effectiveFlightPrice = getEffectiveHotelFlightPrice({
    checkIn,
    flightOriginCode: flightOrigin?.code,
    offer: offer as App.HotelOffer,
    totalFlightPrice,
  })
  const hasEffectiveFlightPrice = effectiveFlightPrice > 0
  const pricingWithFlights = hasEffectiveFlightPrice ? addFlightPrice(hotelPricing, effectiveFlightPrice) : undefined
  const memberPricingWithFlights = (hotelMemberPricing && hasEffectiveFlightPrice) ? addFlightPrice(hotelMemberPricing, effectiveFlightPrice) : undefined

  const {
    hotelPrice: multipleRoomHotelPrice,
    hotelMemberPrice: multipleRoomHotelMemberPrice,
  } = multiRoomPackageTotals
  let {
    hotelPlusFlightsPrice: multipleRoomFlightPrice,
    hotelPlusFlightsMemberPrice: multipleRoomMemberFlightPrice,
  } = multiRoomPackageTotals
  const hasMultiRoomHotelMemberPrice = multipleRoomHotelMemberPrice > 0

  if (hasEffectiveFlightPrice) {
    multipleRoomFlightPrice = multipleRoomHotelPrice + effectiveFlightPrice
    multipleRoomMemberFlightPrice = hasMultiRoomHotelMemberPrice
      ? multipleRoomHotelMemberPrice + effectiveFlightPrice
      : 0
  }

  const hotelTotalPrice = isMultiRoom ? multipleRoomHotelPrice : hotelPricing.price
  const hotelMemberTotalPrice = isMultiRoom
    ? (hasMultiRoomHotelMemberPrice ? multipleRoomHotelMemberPrice : undefined)
    : hotelMemberPricing?.price
  const hotelFlightTotalPrice = isMultiRoom ? multipleRoomFlightPrice : pricingWithFlights?.price ?? 0
  const hotelFlightMemberTotalPrice = isMultiRoom
    ? (multipleRoomMemberFlightPrice > 0 ? multipleRoomMemberFlightPrice : undefined)
    : memberPricingWithFlights?.price

  const showMemberPrice = !!memberPricingToDisplay && memberPricingToDisplay.price > 0
  const showDiscountPill = !offer?.isDiscountPillHidden && pricingToDisplay && pricingToDisplay.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const showPriceDetails = (fees.taxesAndFees ?? 0) > 0 || (fees.property ?? 0) > 0 || config.agentHub.isEnabled
  const flightsEnabled = !!flightOrigin && offer.offerFlightsEnabled
  const flightPricePending = !!checkIn && flightPriceLoading && flightsEnabled && !offer.flightCacheDisabled
  const hasFlights = (hasEffectiveFlightPrice || flightPricePending) && !offer.flightCacheDisabled
  const hasDisplayPriceAndCta = hasFlights && !flightPricePending
  const showFlightsDealTag = flightsEnabled && flightOrigin && flightDestination && deals.length > 0

  const showSecureWithDepositTag = isDepositAvailableOnOffer(offer, currentCurrency, checkInMoment, hotelTotalPrice)
  const isReserveForZeroEnabled = config.RESERVE_FOR_ZERO_ENABLED
  const isReserveForZeroTagShown = (isReserveForZeroEnabled || isSpoofed) && isPackageEligibleForZeroReservation(offer, pkg.roomRate?.isReservableForZero, checkInMoment)
  const isDepositTagShown = showSecureWithDepositTag

  const saleUnit = isMultiRoom ? 'total' : pricingToDisplay?.saleUnit ?? offer.saleUnit

  useEffect(() => {
    appDispatch(setCustomerValue(hotelTotalPrice))
  }, [appDispatch, hotelTotalPrice])

  const onViewHotelOptions = useCallback(() => {
    trackOfferPagePricingBlockViewOptionsClick('hotel')
    dispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected: false })
    onViewPackageOptions()
  }, [dispatch, onViewPackageOptions])

  const onViewHotelFlightOptions = useCallback(() => {
    trackOfferPagePricingBlockViewOptionsClick('hotel_plus_flights')
    dispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected: true })
    onViewPackageOptions()
  }, [dispatch, onViewPackageOptions])

  const onChangeAirport = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'airport' })
  }, [dispatch])

  const isRewardSelected = rewardAvailable && rewardSeatPointsTotal != null
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const rewardLockedForLogin = isRewardSelected && !memberId
  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode, nights: packageView?.package?.duration }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView,
      skip: !hasFlights || !flightsEnabled || !offer.bundledWithFlightsOnly || isRewardSelected,
    }),
  ], [offer, packageView, hasFlights, flightsEnabled, isRewardSelected, currentRegionCode])

  const shouldTrackPrice = !calendarsLoading && showPrices && !!hotelPricing?.price
  const priceTrackedRef = useRef(false)
  const { searchId } = useQsQueryParams<{ searchId?: string }>()
  useEffect(() => {
    if (shouldTrackPrice && searchId && offer?.id && !priceTrackedRef.current) {
      trackClientEvent({
        subject: `searchId:${searchId}_offerId:${offer.id}`,
        action: hotelTotalPrice.toString(),
        category: 'price_accuracy',
        type: 'operational',
      })
      priceTrackedRef.current = true
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrackPrice, hotelTotalPrice, offer?.id])

  const hotelFlightsSummaryRef = useHotelFlightsCtaImpression(hasDisplayPriceAndCta)

  const convertToDisplay = getPriceDisplayFunction(priceMode, rooms)

  const addFlightsMessage = isIndiaRegion
    ? intl.formatMessage({
        id: 'N8LpOQH2J7wEHNIeDQFZ',
        defaultMessage: 'Add flights to your hotel package from the best available airlines.',
        description: 'Message to add flights for India region in the flight search or booking flow',
      })
    : intl.formatMessage({
        id: '8Ckjqv-VFy9GoJ6Jx_rB',
        defaultMessage: 'Add flights to the hotel package. Select from all major airlines at the next step.',
        description: 'Message to add flights for non-India regions in the flights flow',
      })

  const nightsLabel = intl.formatMessage(
    {
      id: 'kdrYhCYkSkMc9tWSL1Nx',
      defaultMessage: '{count, plural, one {# night} other {# nights}}',
      description: 'Night count for hotel summary pricing on the hotel or room booking page',
    },
    { count: duration },
  )
  const roomsLabel = intl.formatMessage(
    {
      id: 'qwe-SJyRoObIWR6cYjov',
      defaultMessage: '{count, plural, one {# room} other {# rooms}}',
      description: 'Room count for hotel summary pricing on the hotel or room booking page',
    },
    { count: rooms.length },
  )
  const hotelPriceCaption = rooms.length > 1
    ? intl.formatMessage(
        {
          id: 'EQljMByoOwAzfSiFjku3',
          defaultMessage: '{nightsLabel}, {roomsLabel} from',
          description:
            'Caption above the multi-room hotel price in the flash offer summary, combining localized night and room counts with the from label',
        },
        {
          nightsLabel,
          roomsLabel,
        },
      )
    : intl.formatMessage(
        { id: '-DvYusGqotHrsAkSnNar', defaultMessage: '{count, plural, one {# night} other {# nights}} from', description: 'Duration for single-room hotel summary pricing in the flights section' },
        { count: duration },
      )
  const hotelFlightsPriceCaption = rooms.length > 1
    ? intl.formatMessage(
        {
          id: 'Oa6wrZpoMhqDZJQANAUw',
          defaultMessage: '{nightsLabel}, {roomsLabel} & flights from',
          description:
            'Caption above the multi-room hotel and flights price in the flash offer summary, combining localized night and room counts with the flights label',
        },
        {
          nightsLabel,
          roomsLabel,
        },
      )
    : intl.formatMessage(
        { id: '0VIq41hkDyuCCzO9LFmO', defaultMessage: '{count, plural, one {# night} other {# nights}} & flights from', description: 'Duration with flights for single-room hotel summary pricing' },
        { count: duration },
      )

  return (
    <Pane borderRadius="S" shadow="bottom-small" className={className}>
      <SummaryPane borderRadius="S" border="light-foreground" padding="28 20" data-testid="SummaryPane">
        <LoadingIndicator floating backdrop="grey-light" opaque visible={!!calendarsLoading} />
        <WalledContent
          enforced={offer.walledGarden}
          wall={<BodyText variant="large" weight="bold">
            {intl.formatMessage(
              {
                id: 'lO1qCISjVW5fd5JZabVg',
                defaultMessage: '{durationLabel} packages available',
                description: 'Text showing available packages with duration label',
              },
              {
                durationLabel: offer.durationLabel,
              },
            )}
          </BodyText>}
        >
          <Group direction="vertical" gap={16} horizontalAlign="center">
            <Group direction="vertical" gap={8}>
              <Group direction="horizontal" gap={4} wrap="wrap">
                {showDiscountPill && !!pricingToDisplay?.discountPercent && <PriceRowValueDiscount
                  type="estimate"
                  discountPercentage={pricingToDisplay.discountPercent}
                />}
                {isDepositTagShown && !isSpoofed && <SecureWithDepositTagAndTooltip offerType={offer.type} />}
                {isTimeLimited(offer) && <OfferTimeLimitedText />}
                {isReserveForZeroTagShown && <ReserveForZeroTag />}
              </Group>
              <AccommodationPackageTilePricingBlocks>
                <Group direction="vertical" gap={12} fullHeight verticalAlign="space-between">
                  <Group direction="vertical" gap={12}>
                    <BodyText variant="large" weight="bold">
                      {intl.formatMessage(productLabels.hotel)}
                    </BodyText>
                    {showPrices && hotelPricing &&
                      <Group direction="vertical" gap={16}>
                        <Group direction="vertical" gap={4}>
                          <PriceRowPriceCaption>
                            {hotelPriceCaption}
                          </PriceRowPriceCaption>
                          <LuxPlusPriceStack
                            size="S"
                            price={convertToDisplay(hotelTotalPrice)}
                            memberPrice={hotelMemberTotalPrice && convertToDisplay(hotelMemberTotalPrice)}
                            saleUnit={priceMode === 'perPerson' ? 'person' : saleUnit}
                            testid="hotel-summary-price"
                            offerConfig={offer.luxPlus}
                          />
                        </Group>
                      </Group>}
                  </Group>
                  <TextButton
                    kind="secondary"
                    data-testid="OfferAvailable"
                    onClick={onViewHotelOptions}
                    fit="flex"
                  >
                    {intl.formatMessage({
                      id: '0mKq4wtIU6k01Jb6o4RX',
                      defaultMessage: 'View options',
                      description: 'Button text to view the available package or room options for the current offer',
                    })}
                  </TextButton>
                </Group>
                <Group ref={hotelFlightsSummaryRef} direction="vertical" gap={12} fullHeight verticalAlign="space-between">
                  <Group direction="vertical" gap={12}>
                    <BodyTextBlock startIcon={<LinePlaneDepartureIcon />} variant="large" weight="bold">
                      {intl.formatMessage({
                        id: 'X8D4ik8z1J1eTsfpSfvV',
                        defaultMessage: 'Hotel + flights',
                        description: 'Label for hotel and flights pricing section in the flights flow',
                      })}
                    </BodyTextBlock>
                    {showPrices && hasFlights &&
                      <Group direction="vertical" gap={16}>
                        <Group direction="vertical" gap={4}>
                          <PriceRowPriceCaption>
                            {hotelFlightsPriceCaption}
                          </PriceRowPriceCaption>
                          {rewardLockedForLogin && (
                            <BodyText variant="medium" colour="neutral-two">
                              {intl.formatMessage({
                                id: 'zp0O9z2ci6Dk9LPEDw0u',
                                defaultMessage: 'Log in to view',
                                description: 'Message shown on reward fare cards when the user is not logged in',
                              })}
                            </BodyText>
                          )}
                          {flightPricePending && <div data-testid="hotel-flights-summary-price-loading">
                            <PriceRowPriceLoadingSkeleton size="S" />
                          </div>}
                          {!flightPricePending && !rewardLockedForLogin && rewardAvailable && rewardSeatPointsTotal != null && rewardSeatCashCoPayTotal != null && (() => {
                            const rewardPrice = computeRewardBundlePrice({
                              hotelPrice: hotelPricing.price,
                              hotelMemberPrice: hotelMemberPricing?.price,
                              rewardPointsTotal: rewardSeatPointsTotal,
                              rewardCashCoPayTotal: rewardSeatCashCoPayTotal,
                            })
                            const isMemberReward = rewardPrice.memberCash !== undefined
                            const rewardCashTotal = rewardPrice.memberCash ?? rewardPrice.cash
                            const nonMemberCashTotal = rewardPrice.cash
                            return (
                              <VerticalSpacer gap={4}>
                                <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap" data-testid="hotel-flights-summary-price-reward">
                                  <Heading variant="heading6" colour={isMemberReward ? 'lux-plus' : undefined}>
                                    <FormattedMessage
                                      id="DoQ7Al24Uo1TlhffLtxM"
                                      defaultMessage="{points} + {cash}"
                                      description="Reward-seat bundle price on the Flash hotel with-flights summary, showing the loyalty points portion and the cash co-pay portion combined as one heading."
                                      values={{
                                        points: <FormatLoyaltyPoints points={rewardSeatPointsTotal} abbreviated />,
                                        cash: <FormatCurrency value={rewardCashTotal} format="roundedDollar" />,
                                      }}
                                    />
                                  </Heading>
                                </Group>
                                {isMemberReward && (
                                  <Caption variant="medium" colour="neutral-two">
                                    <FormattedMessage
                                      id="dDulXESd_Db1yZjxr1Y5"
                                      defaultMessage="Non-member {price}"
                                      description="Price caption label for non-member price display in offer page"
                                      values={{
                                        price: <b><FormattedMessage id="Gc4tuZr5WpQJP1ghBF03" defaultMessage="{points} + {cash}" description="Points plus cash for the non-member reward price on the with-flights summary section" values={{ points: <FormatLoyaltyPoints points={rewardSeatPointsTotal} abbreviated />, cash: <FormatCurrency value={nonMemberCashTotal} format="roundedDollar" /> }} /></b>,
                                      }}
                                    />
                                  </Caption>
                                )}
                              </VerticalSpacer>
                            )
                          })()}
                          {!flightPricePending && !rewardLockedForLogin && !(rewardAvailable && rewardSeatPointsTotal != null && rewardSeatCashCoPayTotal != null) && (
                            <LuxPlusPriceStack
                              size="S"
                              price={convertToDisplay(hotelFlightTotalPrice)}
                              memberPrice={hotelFlightMemberTotalPrice && convertToDisplay(hotelFlightMemberTotalPrice)}
                              saleUnit={priceMode === 'perPerson' ? 'person*' : 'total*'}
                              testid="hotel-flights-summary-price"
                              offerConfig={offer.luxPlus}
                            />
                          )}
                        </Group>
                        {showFlightsDealTag && activeDeal && <FlightDealLabel activeDeal={activeDeal} showCountdown={false} />}
                      </Group>}
                    {!hasFlights && flightOrigin?.code === flightDestination?.code &&
                      <PriceRowCaption>
                        {intl.formatMessage(
                          {
                            id: 'e543oU2rlSWn9p_jiHgI',
                            defaultMessage: 'No flights available from {airportName}. Please select another airport.',
                            description: 'Message when no flights available from selected airport',
                          },
                          {
                            airportName: flightOrigin?.name ?? '',
                          },
                        )}
                      </PriceRowCaption>}
                    {!hasFlights && flightOrigin?.code !== flightDestination?.code && !departureTooFarInFuture &&
                      <PriceRowCaption>
                        {addFlightsMessage}
                      </PriceRowCaption>}
                    {!hasFlights && departureTooFarInFuture &&
                      <PriceRowCaption>
                        {intl.formatMessage({
                          id: 'SjlpFqk2nfjBfQqW7_O9',
                          defaultMessage: 'Flights can only be booked up to 11 months in advance.',
                          description: 'Notification message displayed when departure date is too far in future',
                        })}
                      </PriceRowCaption>}
                  </Group>
                  {hasFlights && (
                    <TextButton
                      kind="secondary"
                      data-testid="OfferAvailable"
                      onClick={onViewHotelFlightOptions}
                      fit="flex"
                    >
                      {intl.formatMessage({
                        id: '0mKq4wtIU6k01Jb6o4RX',
                        defaultMessage: 'View options',
                        description: 'Button text to view the available package or room options for the current offer',
                      })}
                    </TextButton>
                  )}
                  {!hasFlights && flightOrigin?.code === flightDestination?.code && (
                    <TextButton kind="tertiary" fit="flex" onClick={onChangeAirport}>
                      {intl.formatMessage({
                        id: 'FJeKASDKevfTCPbRvSz7',
                        defaultMessage: 'Change airport',
                        description: 'Button text to change the selected departure or arrival airport in the flight form',
                      })}
                    </TextButton>
                  )}
                </Group>
              </AccommodationPackageTilePricingBlocks>
            </Group>
            <FlashHotelFlightDealMerchandisingSlot
              flightOrigin={flightOrigin}
              flightDestination={flightDestination}
              deals={deals}
              hasFlights={hasFlights}
              disabled={!flightsEnabled}
              allowCalculatedFallback
            />
            <LoyaltyPointsEarn
              horizontalAlign="center"
              calculationRequests={pointsEarnCalculationRequests}
              calculationType="estimate"
              offer={offer}
            />
            <PriceRowTaxesAndFees
              endIcon={showPriceDetails && <OfferPriceDetailsRow
                offerPage
                offer={offer}
                duration={pkg.duration}
                propertyFees={fees.property}
                taxesAndFees={fees.taxesAndFees}
                hotelPrice={priceWithoutPropertyFees(hotelPricing)}
                hotelMemberPrice={hotelMemberPricing ? priceWithoutPropertyFees(hotelMemberPricing) : undefined}
                showMemberPrice={showMemberPrice}
                rooms={rooms}
                extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
                dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
                trigger="info-icon"
                cta={<TextButton kind="primary" fit="flex">
                  {intl.formatMessage({
                    id: '0mKq4wtIU6k01Jb6o4RX',
                    defaultMessage: 'View options',
                    description: 'Button text to view the available package or room options for the current offer',
                  })}
                </TextButton>}
                onCtaAfterClose={onViewPackageOptions}
              />}
            />
          </Group>
        </WalledContent>
      </SummaryPane>
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)
  return {
    cheapestSelectedOption: getCheapestSelectedPackageOptionWithDefault(appState, state),
    rooms: state.rooms,
    offer: state.offer,
    checkIn: state.checkInDate,
    calendarsLoading: arePricesLoading(appState, state),
    isBNBLAvailable: isBNBLAvailable(state),
    isSpoofed: appState.auth.account.isSpoofed,
    duration: state.duration,
    flightOrigin: state.airport,
    isMultiRoom,
    multiRoomPackageTotals: isMultiRoom ? getMultiRoomPackageTotals(state, appState) : defaultMultiRoomTotals,
    priceMode: state.priceMode,
  }
}, FlashOfferWithFlightsSummary)
